<template>
  <div class="pam-nom-wrap">
    <!-- Mobil: listă de carduri + popup simplu -->
    <PamCardList v-if="isMobile"
      :items="items"
      :title-field="{ key: 'Nume' }"
      :fields="cardFields"
      :empty-text="'Niciun element.'"
      @add="onAdd" @edit="onEdit" @delete="onDelete" />

    <DxDataGrid
      v-else
      :data-source="ds"
      key-expr="ID"
      height="100%"
      :show-borders="true"
      :show-column-lines="false"
      :show-row-lines="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :column-auto-width="false"
      @init-new-row="onInitNewRow"
    >
      <DxSelection mode="single" />
      <DxFilterRow :visible="true" />
      <DxSearchPanel :visible="true" placeholder="Caută..." />
      <DxPaging :page-size="15" />
      <DxSorting mode="multiple" />
      <DxEditing :allow-adding="true" :allow-updating="true" :allow-deleting="true" mode="popup">
        <DxPopup :show-title="true" :width="420" height="auto" title-template="editTitle" :close-on-outside-click="false" />
        <DxForm :col-count="1" />
      </DxEditing>
      <template #editTitle>
        <div class="pam-nom-title">{{ title }}</div>
      </template>

      <DxColumn data-field="ID" caption="ID" :width="90" :allow-editing="false" :visible="false" :form-item="{ visible: false }" />
      <DxColumn v-if="marcaSource" data-field="MarcaID" caption="Marcă">
        <DxLookup :data-source="marcaSource" display-expr="Nume" value-expr="ID" />
        <DxRequiredRule message="Marca este obligatorie" />
      </DxColumn>
      <DxColumn data-field="Nume" caption="Nume">
        <DxRequiredRule message="Numele este obligatoriu" />
      </DxColumn>
      <DxColumn v-if="showCod" data-field="Cod" caption="Cod" :visible="false" :form-item="{ visible: false }" />

      <DxToolbar>
        <DxItem name="addRowButton" />
        <DxItem name="searchPanel" />
      </DxToolbar>
    </DxDataGrid>

    <!-- Popup mobil (add/edit) -->
    <DxPopupStd :visible="popupVisible" :show-title="true" :full-screen="isMobile" :width="420" height="auto"
      :close-on-outside-click="false" :show-close-button="true" @hidden="popupVisible = false">
      <template #title>
        <div class="pam-nom-title">{{ title }}</div>
      </template>
      <DxFormStd ref="formRef" :form-data="formData" :col-count="1" label-location="top">
        <DxSimpleItem v-if="marcaSource" data-field="MarcaID" :label="{ text: 'Marcă' }" editor-type="dxSelectBox"
          :editor-options="{ dataSource: marcaSource, displayExpr: 'Nume', valueExpr: 'ID', searchEnabled: true }">
          <DxRequiredRuleStd message="Marca este obligatorie" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Nume" :label="{ text: 'Nume' }">
          <DxRequiredRuleStd message="Numele este obligatoriu" />
        </DxSimpleItem>
      </DxFormStd>
      <div class="pam-nom-footer">
        <DxButton text="Salvează" type="default" icon="save" width="120" :disabled="isSaving" @click="onSave" />
        <DxButton text="Anulează" type="normal" styling-mode="outlined" width="120" @click="popupVisible = false" />
      </div>
    </DxPopupStd>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import ODataStore from "devextreme/data/odata/store";
import DataSource from "devextreme/data/data_source";
import DxDataGrid, {
  DxColumn, DxLookup, DxSelection, DxFilterRow, DxSearchPanel, DxPaging, DxSorting,
  DxEditing, DxPopup, DxForm, DxRequiredRule, DxToolbar, DxItem,
} from "devextreme-vue/data-grid";
import DxPopupStd from "devextreme-vue/popup";
import DxFormStd, { DxSimpleItem, DxRequiredRule as DxRequiredRuleStd } from "devextreme-vue/form";
import DxButton from "devextreme-vue/button";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import AppSettings from "@/settings/appSettings";
import PamCardList from "../PamCardList.vue";

export default defineComponent({
  name: "GridNomenclatorPam",
  components: {
    DxDataGrid, DxColumn, DxLookup, DxSelection, DxFilterRow, DxSearchPanel, DxPaging, DxSorting,
    DxEditing, DxPopup, DxForm, DxRequiredRule, DxToolbar, DxItem,
    DxPopupStd, DxFormStd, DxSimpleItem, DxRequiredRuleStd, DxButton, PamCardList,
  },
  props: {
    entity: { type: String, required: true },
    title: { type: String, required: true },
    showCod: { type: Boolean, default: true },
    marcaSource: { type: Array as PropType<any[]>, required: false, default: undefined },
  },
  data() {
    const store = new ODataStore({
      url: AppSettings.API_Url_ParcAutoOdata + this.entity,
      key: "ID",
      keyType: "Int32",
      version: 4,
      beforeSend: (e: any) => {
        e.headers = {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("ERA_Jwt"),
        };
      },
    });
    return {
      store,
      ds: new DataSource({ store, paginate: true, pageSize: 15 }),
      items: [] as any[],
      isMobile: window.matchMedia("(max-width: 768px)").matches,
      popupVisible: false,
      isEdit: false,
      isSaving: false,
      formData: {} as Record<string, any>,
    };
  },
  computed: {
    mMarca(): Record<number, string> {
      return Object.fromEntries(((this.marcaSource as any[]) || []).map((x) => [x.ID, x.Nume]));
    },
    cardFields(): any[] {
      if (!this.marcaSource) return [];
      return [{ label: "Marcă", key: "MarcaID", format: (v: any) => this.mMarca[v] ?? null }];
    },
  },
  created() {
    if (this.isMobile) this.loadItems();
  },
  methods: {
    async loadItems() {
      try {
        this.items = Object.freeze((await this.store.load({})) as any[]) as any[];
      } catch { this.items = []; }
    },
    onInitNewRow(e: any) {
      // ID e identity - nu îl trimitem la insert
      delete e.data.ID;
    },
    onAdd() {
      this.isEdit = false;
      this.formData = {};
      this.popupVisible = true;
    },
    onEdit(row: any) {
      this.isEdit = true;
      this.formData = { ...row };
      this.popupVisible = true;
    },
    async onDelete(row: any) {
      const ok = await confirm("Ștergeți acest element?", "Confirmare");
      if (!ok) return;
      try {
        await this.store.remove(row.ID);
        notify("Șters.", "success", 1500);
        this.loadItems();
      } catch (err: any) {
        notify(err?.message || "Ștergere eșuată.", "error", 4000);
      }
    },
    async onSave() {
      const form = (this.$refs.formRef as any)?.instance;
      const res = form?.validate();
      if (res && res.isValid === false) return;
      this.isSaving = true;
      try {
        const payload: Record<string, any> = { ...this.formData };
        if (this.isEdit) {
          const id = payload.ID;
          delete payload.ID;
          await this.store.update(id, payload);
        } else {
          delete payload.ID;
          await this.store.insert(payload);
        }
        notify("Salvat.", "success", 1500);
        this.popupVisible = false;
        this.loadItems();
      } catch (err: any) {
        notify(err?.message || "Salvare eșuată.", "error", 4000);
      } finally {
        this.isSaving = false;
      }
    },
  },
});
</script>

<style scoped>
.pam-nom-wrap { height: 100%; width: 100%; }
.pam-nom-title { background: linear-gradient(90deg, #ff2c21, #ff6a4a); color: #fff; font-weight: bold; font-size: 15px; padding: 10px 20px; width: 100%; }
.pam-nom-footer { display: flex; justify-content: flex-end; gap: 10px; padding-top: 16px; }
</style>
